// Schema Zod del search param de la ruta /facturacion. Sirve a TanStack
// Router como `validateSearch` y al frontend como single source of truth
// del shape de la URL.
//
// La URL canónica:
//   /facturacion?tab=caja&entidad=consolidado
//   /facturacion?tab=facturacion&entidad=<uuid-ltda>&estado=emitida&q=acme

import { z } from 'zod';

export const INVOICING_TABS = [
  'caja',
  'facturacion',
  'masiva',
  'revision_contratos',
  'cola',
  'conciliacion',
  'cobranza',
  'ingresos',
] as const;
export type InvoicingTab = (typeof INVOICING_TABS)[number];

/**
 * `entidad` es 'consolidado' (todas las entidades agregadas) o el UUID
 * de una entidad facturadora puntual. Cuando hay un UUID, los KPIs y
 * lists filtran por `entidadFacturadoraId=<uuid>`.
 */
const entidadSchema = z.string().min(1).default('consolidado');

const tabSchema = z.enum(INVOICING_TABS).default('caja');

export const invoicingSearchSchema = z.object({
  tab: tabSchema,
  entidad: entidadSchema,
  /** Filter de estado para vistas que lo soporten (Caja, Facturación). */
  estado: z.string().optional(),
  /** Filter por tipo DTE (factura / factura_exenta / nota_credito / ...). */
  tipoDte: z.string().optional(),
  /** Rango sobre fecha_emision — alimenta KPIs y lista por igual. */
  fechaDesde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  fechaHasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Texto de búsqueda libre — debe servir tanto para folio como para cliente. */
  q: z.string().optional(),
  /**
   * ID de la invoice que está abierta en el detail drawer (ETAPA 8). Si
   * está set, monta el drawer; al cerrarlo, se elimina el param.
   */
  selectedInvoiceId: z.string().uuid().optional(),
  /**
   * ID del cliente que está abierto en el client-financial-drawer
   * (ETAPA 9). Independiente de selectedInvoiceId — ambos drawers
   * pueden coexistir (uno arriba del otro vía Vaul stacking).
   */
  selectedClientId: z.string().uuid().optional(),
  /**
   * Si '1', muestra el invoice form dialog (ETAPA 10) sobre la página.
   * Bool-as-literal para que el back/forward del browser preserve
   * el estado modal.
   */
  newInvoice: z.literal('1').optional(),
  /**
   * ID de la factura sobre la que se está creando una NC (ETAPA 11).
   * Activa el nc-form-drawer y precarga items desde esa invoice.
   */
  ncForInvoiceId: z.string().uuid().optional(),
  /** Saved views multi-módulo — id de la vista activa. */
  viewId: z.string().uuid().optional(),
  /**
   * Período YYYY-MM para el filtro de fecha_emisión en list views. Sin
   * este filtro, el backend retorna las últimas 50 facturas por createdAt
   * DESC — con >300 facturas en el mes actual, los meses anteriores nunca
   * aparecen. Cuando hay periodo, el list-view eleva el limit a 500 para
   * mostrar el mes completo.
   */
  periodo: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Período debe ser YYYY-MM')
    .optional(),
  /**
   * Sub-tab del tab Conciliación: 'cola' (default) o 'historico' (vista
   * Conciliados). Lifteado al URL para que el toast post-conciliación y
   * la KPI card "Conciliados este mes" puedan navegar al sub-tab desde
   * fuera del componente.
   */
  concSubTab: z.enum(['cola', 'historico']).optional(),
});

export type InvoicingSearch = z.infer<typeof invoicingSearchSchema>;

export const INVOICING_TAB_LABELS: Record<InvoicingTab, string> = {
  caja: 'Caja',
  facturacion: 'Facturación',
  masiva: 'Emisión masiva',
  revision_contratos: 'Revisión contratos',
  cola: 'Cola',
  conciliacion: 'Conciliación',
  cobranza: 'Cobranza',
  ingresos: 'Ingresos',
};
